import { featureHeight, featureSurface } from './features';
import { CELL_SIZE, CHUNK_CELLS, CHUNK_SIZE, CHUNK_VERTS } from './grid';
import { grooveHeight, Level, padDistance, smoothstep } from './level';
import { hash2 } from './noise';
import { Heightfield, SURFACES } from './terrain';

/**
 * Regression guard for one chunk in Node: measured ~135 ms on the dev machine (2026-09-28) for a chunk
 * with ~700 m of track and a forest, budget = 2.5x. Chunks load ahead of the rider, so this is not a
 * frame budget; generateChunk takes plain data and returns typed arrays so it can move to a worker.
 */
export const CHUNK_BUDGET_MS = 350;

export interface ChunkTrees { spacing: number; perSide: number; xs: Float32Array; zs: Float32Array; radius: Float32Array }
export interface ChunkData { ci: number; cj: number; field: Heightfield; trees: ChunkTrees | null }

const COARSE = 4;
const CV = CHUNK_CELLS / COARSE + 1;
const N = CHUNK_VERTS * CHUNK_VERTS;
let best: Float32Array, seg: Int32Array, segT: Float32Array, lat: Float32Array, coarse: Float32Array, onPath: Uint8Array;
let pathDist: Float32Array, pathIdx: Int16Array, padIdx: Int8Array;
function scratch(): void {
  if (best) return;
  best = new Float32Array(N); seg = new Int32Array(N); segT = new Float32Array(N); lat = new Float32Array(N);
  coarse = new Float32Array(CV * CV); onPath = new Uint8Array(N);
  pathDist = new Float32Array(N); pathIdx = new Int16Array(N); padIdx = new Int8Array(N);
}

/**
 * Index (into `pads`) of the first pad covering each vertex, -1 for none: the same answer as Level.padSurface at
 * every vertex, but each pad is only tested over its bounding box (testing all pads at all ~1M vertices of a chunk
 * cost seconds on a laptop, on the main thread whenever a chunk was needed before the worker delivered it).
 */
function markPads(pads: Level['def']['pads'], ox: number, oz: number): void {
  padIdx.fill(-1);
  for (let p = 0; p < pads.length; p++) {
    const pad = pads[p]!;
    const r = pad.radius + (pad.length ?? 0) / 2 + CELL_SIZE;
    const i0 = Math.max(0, Math.floor((pad.x - r - ox) / CELL_SIZE)), i1 = Math.min(CHUNK_CELLS, Math.ceil((pad.x + r - ox) / CELL_SIZE));
    const j0 = Math.max(0, Math.floor((pad.z - r - oz) / CELL_SIZE)), j1 = Math.min(CHUNK_CELLS, Math.ceil((pad.z + r - oz) / CELL_SIZE));
    for (let j = j0; j <= j1; j++) {
      for (let i = i0; i <= i1; i++) {
        const v = j * CHUNK_VERTS + i;
        if (padIdx[v]! < 0 && padDistance(pad, ox + i * CELL_SIZE, oz + j * CELL_SIZE) <= pad.radius) padIdx[v] = p;
      }
    }
  }
}

/** Edge lips reach this far outside a path. */
const LIP_REACH = 1.2;

/**
 * Marks vertices within half a width of each path with (surface id + 1), and records for vertices up to LIP_REACH
 * beyond the edge the distance to the nearest path line and which path it is (-1: none), for grooves and lips.
 */
function markPaths(level: Level, ox: number, oz: number): boolean {
  onPath.fill(0);
  pathDist.fill(Infinity);
  pathIdx.fill(-1);
  let any = false;
  level.paths.forEach((p, pi) => {
    const { line } = p, half = p.half + LIP_REACH, n = line.count;
    for (let k = 0; k < n; k++) {
      const k1 = (k + 1) % n;
      const ax = line.xs[k]!, az = line.zs[k]!, bx = line.xs[k1]!, bz = line.zs[k1]!;
      const i0 = Math.max(0, Math.ceil((Math.min(ax, bx) - half - ox) / CELL_SIZE));
      const i1 = Math.min(CHUNK_CELLS, Math.floor((Math.max(ax, bx) + half - ox) / CELL_SIZE));
      const j0 = Math.max(0, Math.ceil((Math.min(az, bz) - half - oz) / CELL_SIZE));
      const j1 = Math.min(CHUNK_CELLS, Math.floor((Math.max(az, bz) + half - oz) / CELL_SIZE));
      if (i0 > i1 || j0 > j1) continue;
      const ex = bx - ax, ez = bz - az, len2 = ex * ex + ez * ez;
      for (let j = j0; j <= j1; j++) {
        const pz = oz + j * CELL_SIZE - az;
        for (let i = i0; i <= i1; i++) {
          const px = ox + i * CELL_SIZE - ax;
          let t = (px * ex + pz * ez) / len2;
          t = t < 0 ? 0 : t > 1 ? 1 : t;
          const qx = px - t * ex, qz = pz - t * ez;
          const d2 = qx * qx + qz * qz;
          if (d2 > half * half) continue;
          const v = j * CHUNK_VERTS + i, dist = Math.sqrt(d2);
          if (dist < pathDist[v]!) { pathDist[v] = dist; pathIdx[v] = pi; }
          if (dist <= p.half) { onPath[v] = p.surface + 1; any = true; }
        }
      }
    }
  });
  return any;
}

/** Height added by a path's grooves and edge lips at distance `dist` from its line (grooves are symmetric). */
function pathShape(p: Level['paths'][number], dist: number): number {
  let h = 0;
  if (p.def.grooves && dist < p.half) { h += grooveHeight(p.def.grooves, dist); h += grooveHeight(p.def.grooves, -dist); }
  if (p.def.lip) h += p.def.lip * Math.exp(-(((dist - p.half) / 0.45) ** 2));
  return h;
}

export function generateChunk(level: Level, ci: number, cj: number): ChunkData {
  scratch();
  const ox = ci * CHUNK_SIZE, oz = cj * CHUNK_SIZE;
  const field = new Heightfield(CHUNK_CELLS, CELL_SIZE, ox, oz);
  const cstep = CELL_SIZE * COARSE;
  for (let b = 0; b < CV; b++) for (let a = 0; a < CV; a++) coarse[b * CV + a] = level.ground(ox + a * cstep, oz + b * cstep);

  const R = level.influence;
  best.fill(Infinity);
  const line = level.line;
  if (line) {
    const n = line.count;
    for (let k = 0; k < n; k++) {
      const k1 = (k + 1) % n;
      const ax = line.xs[k]!, az = line.zs[k]!, bx = line.xs[k1]!, bz = line.zs[k1]!;
      const i0 = Math.max(0, Math.ceil((Math.min(ax, bx) - R - ox) / CELL_SIZE));
      const i1 = Math.min(CHUNK_CELLS, Math.floor((Math.max(ax, bx) + R - ox) / CELL_SIZE));
      const j0 = Math.max(0, Math.ceil((Math.min(az, bz) - R - oz) / CELL_SIZE));
      const j1 = Math.min(CHUNK_CELLS, Math.floor((Math.max(az, bz) + R - oz) / CELL_SIZE));
      if (i0 > i1 || j0 > j1) continue;
      const ex = bx - ax, ez = bz - az, len2 = ex * ex + ez * ez;
      for (let j = j0; j <= j1; j++) {
        const pz = oz + j * CELL_SIZE - az;
        for (let i = i0; i <= i1; i++) {
          const px = ox + i * CELL_SIZE - ax;
          let t = (px * ex + pz * ez) / len2;
          t = t < 0 ? 0 : t > 1 ? 1 : t;
          const qx = px - t * ex, qz = pz - t * ez;
          const dist = Math.sqrt(qx * qx + qz * qz);
          const v = j * CHUNK_VERTS + i;
          if (dist < best[v]!) {
            best[v] = dist; seg[v] = k; segT[v] = t;
            lat[v] = px * ez - pz * ex >= 0 ? dist : -dist;
          }
        }
      }
    }
  }

  const def = level.def;
  const groundId = SURFACES.indexOf(def.ground.surface);
  const hwMax = level.halfWidth, blendMax = hwMax + level.blend;
  const heights = field.heights, surfaces = field.surfaces;
  const L = line?.length ?? 0, step = line?.step ?? 1, count = line?.count ?? 1;
  // Only pads reaching into this chunk are tested per vertex.
  const pads = def.pads.filter((p) => {
    const r = p.radius + (p.length ?? 0) / 2;
    return p.x + r >= ox && p.x - r <= ox + CHUNK_SIZE && p.z + r >= oz && p.z - r <= oz + CHUNK_SIZE;
  });
  const hasPads = pads.length > 0;
  if (hasPads) markPads(pads, ox, oz);
  // Ground class lookups: grid column per vertex column, grid row per vertex row (nearest sample).
  const grid = level.grid;
  const cols = new Int32Array(CHUNK_VERTS);
  if (grid) {
    const m = grid.meta;
    for (let i = 0; i <= CHUNK_CELLS; i++) cols[i] = Math.min(m.nx - 1, Math.max(0, Math.round((ox + i * CELL_SIZE - m.originX) / m.cell)));
  }
  const hasPaths = markPaths(level, ox, oz);
  const grooves = def.track?.grooves;
  for (let j = 0; j <= CHUNK_CELLS; j++) {
    const cb = Math.min(j >> 2, CV - 2), tz = (j - cb * COARSE) / COARSE;
    const rowBase = grid ? Math.min(grid.meta.nz - 1, Math.max(0, Math.round((oz + j * CELL_SIZE - grid.meta.originZ) / grid.meta.cell))) * grid.meta.nx : 0;
    for (let i = 0; i <= CHUNK_CELLS; i++) {
      const ca = Math.min(i >> 2, CV - 2), tx = (i - ca * COARSE) / COARSE;
      const c0 = cb * CV + ca;
      const g = (coarse[c0]! * (1 - tx) + coarse[c0 + 1]! * tx) * (1 - tz) + (coarse[c0 + CV]! * (1 - tx) + coarse[c0 + CV + 1]! * tx) * tz;
      const v = j * CHUNK_VERTS + i;
      let h = g;
      let sid = grid ? level.classSurface[grid.classes[rowBase + cols[i]!]!]! : groundId;
      if (hasPads) {
        const pi = padIdx[v]!;
        if (pi >= 0) sid = SURFACES.indexOf(pads[pi]!.surface);
      }
      if (hasPaths && onPath[v]) sid = onPath[v]! - 1; // riding lines painted on top of graded pads
      // Grooves and lips of riding lines, except on graded clay (the drill lanes stay dead flat for scoring).
      if (hasPaths && pathIdx[v]! >= 0 && !(hasPads && padIdx[v]! >= 0 && pads[padIdx[v]!]!.surface === 'clay')) {
        h += pathShape(level.paths[pathIdx[v]!]!, pathDist[v]!);
      }
      if (best[v]! < blendMax) {
        const k = seg[v]!, t = segT[v]!, d = lat[v]!, ad = best[v]!;
        const hw = level.halfWidthAt[k]! * (1 - t) + level.halfWidthAt[(k + 1) % count]! * t, blendEnd = hw + level.blend;
        const base = level.centreHeight[k]! * (1 - t) + level.centreHeight[(k + 1) % count]! * t;
        let dh = 0;
        let trackId = level.surfaceAt[k]!;
        const fi = level.featureAt[k]!;
        if (fi >= 0) {
          const f = level.features[fi]!;
          let u = (k + t) * step - f.s;
          if (u > L / 2) u -= L; else if (u < -L / 2) u += L;
          dh = featureHeight(f, u, d, hw, level.outer[fi]!);
          const fs = featureSurface(f);
          if (fs && dh !== 0) trackId = SURFACES.indexOf(fs);
        }
        // Worn ruts on the riding line, but not on jump faces (their shape is the jump).
        if (grooves && (fi < 0 || level.features[fi]!.kind === 'berm')) dh += grooveHeight(grooves, d);
        const w = 1 - smoothstep(hw, blendEnd, ad);
        h = g + (base + dh - g) * w;
        if (ad <= hw + 0.5) sid = trackId;
      }
      heights[v] = h;
      surfaces[v] = sid;
    }
  }

  return { ci, cj, field, trees: def.forest ? plantTrees(level, ci, cj) : null };
}

function plantTrees(level: Level, ci: number, cj: number): ChunkTrees {
  const f = level.def.forest!, seed = level.def.seed;
  const perSide = CHUNK_SIZE / f.spacing;
  const xs = new Float32Array(perSide * perSide), zs = new Float32Array(perSide * perSide), radius = new Float32Array(perSide * perSide);
  const ox = ci * CHUNK_SIZE, oz = cj * CHUNK_SIZE;
  for (let b = 0; b < perSide; b++) {
    for (let a = 0; a < perSide; a++) {
      const ga = ci * perSide + a, gb = cj * perSide + b;
      if (hash2(seed + 11, ga, gb) >= level.treeDensity((ga + 0.5) * f.spacing, (gb + 0.5) * f.spacing)) continue;
      const x = (ga + 0.15 + 0.7 * hash2(seed + 12, ga, gb)) * f.spacing;
      const z = (gb + 0.15 + 0.7 * hash2(seed + 13, ga, gb)) * f.spacing;
      const v = Math.round((z - oz) / CELL_SIZE) * CHUNK_VERTS + Math.round((x - ox) / CELL_SIZE);
      if (best[v]! < f.corridor || onPath[v] || level.padSurface(x, z)) continue;
      const k = b * perSide + a;
      xs[k] = x; zs[k] = z;
      radius[k] = f.minRadius + (f.maxRadius - f.minRadius) * hash2(seed + 14, ga, gb);
    }
  }
  return { spacing: f.spacing, perSide, xs, zs, radius };
}
